/**
 * Shared event deduplication utility
 * Prevents duplicate events from being tracked within a time window
 * Used by both useTracking hook and trackAuthEvent function
 */

interface TrackedEvent {
  timestamp: number;
  eventName: string;
  hash: string;
}

// Single shared deduplication store
const trackedEvents: Map<string, TrackedEvent> = new Map();
const DEDUPE_WINDOW_MS = 10000; // 10 seconds - prevent duplicates within this window

/**
 * Create a hash from event name and normalized properties to identify duplicates
 * Properties are normalized (sorted keys) to ensure consistent hashing
 */
function createEventHash(eventName: string, properties: Record<string, any>): string {
  // Normalize properties by sorting keys and removing undefined/null/empty values
  const normalizedProps: Record<string, any> = {};
  
  // Sort keys to ensure consistent hash regardless of property order
  const sortedKeys = Object.keys(properties).sort();
  for (const key of sortedKeys) {
    const value = properties[key];
    // Only include non-empty values
    if (value !== undefined && value !== null && value !== '') {
      normalizedProps[key] = value;
    }
  }
  
  // Include key identifying properties in the hash
  const keyProps = {
    eventName,
    user_id: normalizedProps.user_id || normalizedProps.external_id || '',
    page_path: normalizedProps.page_path || '',
    content_type: normalizedProps.content_type || '',
    transaction_id: normalizedProps.transaction_id || '',
    plan_id: normalizedProps.plan_id || '',
    email: normalizedProps.email || '',
    method: normalizedProps.method || '',
  };
  
  // Create a stable hash from the key properties
  const hashString = JSON.stringify(keyProps);
  return `${eventName}:${hashString}`;
}

/**
 * Check if an event was recently tracked (within dedupe window)
 * Returns true if duplicate, false if new event
 * 
 * @param eventName - Name of the event
 * @param properties - Event properties (should be normalized with user data already merged)
 * @returns true if duplicate, false if new
 */
export function isDuplicateEvent(eventName: string, properties: Record<string, any>): boolean {
  const hash = createEventHash(eventName, properties);
  const now = Date.now();
  const tracked = trackedEvents.get(hash);
  
  if (tracked) {
    const timeSinceTracked = now - tracked.timestamp;
    if (timeSinceTracked < DEDUPE_WINDOW_MS) {
      return true;
    }
    // Remove old entry if outside window
    trackedEvents.delete(hash);
  }
  
  // Record this event
  trackedEvents.set(hash, {
    timestamp: now,
    eventName,
    hash,
  });
  
  // Clean up old entries (older than dedupe window)
  const cutoff = now - DEDUPE_WINDOW_MS;
  const keysToDelete: string[] = [];
  trackedEvents.forEach((event, key) => {
    if (event.timestamp < cutoff) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => trackedEvents.delete(key));
  
  return false;
}

/**
 * Clear all tracked events (useful for testing or reset)
 */
export function clearTrackedEvents(): void {
  trackedEvents.clear();
}

